import { useCallback, useMemo, useState } from 'react';

import { useApi } from '@/contexts/ApiContext';
import { useToast } from '@/contexts/ToastContext';
import { createRbacService } from '@/services/rbac.service';
import {
  ActionName,
  PermissionStatistics,
  ResourceName,
} from '@/types/api-responses';
import { BulkUpdateRequest, Permission, Role } from '@/types/sheets';

export interface UseRbacReturn {
  // State
  roles: Role[];
  loading: {
    roles: boolean;
    statistics: boolean;
    userPermissions: boolean;
    operations: boolean;
  };
  statistics: PermissionStatistics | null;

  // Role Management
  fetchRoles: () => Promise<void>;
  createRole: (roleData: {
    name: string;
    description?: string;
    permissions: Array<{ resource: string; actions: string[] }>;
    isActive?: boolean;
  }) => Promise<boolean>;
  updateRole: (roleData: {
    roleId: string;
    name?: string;
    description?: string;
    permissions?: Array<{ resource: string; actions: string[] }>;
    isActive?: boolean;
  }) => Promise<boolean>;
  deleteRole: (roleName: string) => Promise<boolean>;

  // User Permission Management
  updateUserPermissions: (data: {
    userId: string;
    roleId?: string;
    permissions?: Array<{ resource: string; actions: string[] }>;
  }) => Promise<boolean>;
  revokeUserPermissions: (data: {
    userId: string;
    permissionKeys: string[];
  }) => Promise<boolean>;
  bulkUpdateUserPermissions: (data: BulkUpdateRequest) => Promise<{
    success: boolean;
    results?: Array<{ userId: string; success: boolean; error?: string }>;
  }>;

  // Permission Queries
  getUserPermissions: (userId: string) => Promise<Permission[] | null>;
  getUsersWithPermission: (
    resource: ResourceName,
    action: ActionName
  ) => Promise<any[] | null>;
  fetchRoleHierarchy: () => Promise<Role[] | null>;
  fetchPermissionStatistics: () => Promise<void>;

  // Utility functions
  hasPermission: (
    userPermissions: Permission[],
    resource: ResourceName,
    action: ActionName
  ) => boolean;
  mergePermissions: (...permissionArrays: Permission[][]) => Permission[];
  validateRoleData: (roleData: {
    name: string;
    permissions: Array<{ resource: string; actions: string[] }>;
  }) => { isValid: boolean; errors: string[] };
}

export const useRbac = (): UseRbacReturn => {
  const api = useApi();
  const { showSuccess, showError } = useToast();
  const rbacService = useMemo(() => createRbacService(api), [api]);

  // State
  const [roles, setRoles] = useState<Role[]>([]);
  const [statistics, setStatistics] = useState<PermissionStatistics | null>(
    null
  );
  const [loading, setLoading] = useState({
    roles: false,
    statistics: false,
    userPermissions: false,
    operations: false,
  });

  // Helper function to update loading state
  const updateLoading = useCallback(
    (key: keyof typeof loading, value: boolean) => {
      setLoading(prev => ({ ...prev, [key]: value }));
    },
    []
  );

  // Role Management
  const fetchRoles = useCallback(async () => {
    updateLoading('roles', true);
    try {
      const response = await rbacService.getRoles();
      if (response.success && response.data) {
        setRoles(response.data.roles);
      } else {
        showError('Failed to fetch roles');
      }
    } catch (error) {
      showError('Error fetching roles');
    } finally {
      updateLoading('roles', false);
    }
  }, [rbacService, showError, updateLoading]);

  const createRole = useCallback(
    async (roleData: {
      name: string;
      description?: string;
      permissions: Array<{ resource: string; actions: string[] }>;
      isActive?: boolean;
    }) => {
      // Validate role data
      const validation = rbacService.validateRoleData(roleData);
      if (!validation.isValid) {
        validation.errors.forEach(error => showError(error));
        return false;
      }

      updateLoading('operations', true);
      try {
        const response = await rbacService.createRole(roleData);
        if (response.success) {
          showSuccess('Role created successfully');
          await fetchRoles(); // Refresh roles list
          return true;
        } else {
          showError(response.error?.message || 'Failed to create role');
          return false;
        }
      } catch (error) {
        showError('Error creating role');
        return false;
      } finally {
        updateLoading('operations', false);
      }
    },
    [rbacService, showSuccess, showError, updateLoading, fetchRoles]
  );

  const updateRole = useCallback(
    async (roleData: {
      roleId: string;
      name?: string;
      description?: string;
      permissions?: Array<{ resource: string; actions: string[] }>;
      isActive?: boolean;
    }) => {
      updateLoading('operations', true);
      try {
        const response = await rbacService.updateRole(roleData);
        if (response.success) {
          showSuccess('Role updated successfully');
          await fetchRoles(); // Refresh roles list
          return true;
        } else {
          showError(response.error?.message || 'Failed to update role');
          return false;
        }
      } catch (error) {
        showError('Error updating role');
        return false;
      } finally {
        updateLoading('operations', false);
      }
    },
    [rbacService, showSuccess, showError, updateLoading, fetchRoles]
  );

  const deleteRole = useCallback(
    async (roleName: string) => {
      updateLoading('operations', true);
      try {
        const response = await rbacService.deleteRole(roleName);
        if (response.success) {
          showSuccess('Role deleted successfully');
          await fetchRoles(); // Refresh roles list
          return true;
        } else {
          showError(response.error?.message || 'Failed to delete role');
          return false;
        }
      } catch (error) {
        showError('Error deleting role');
        return false;
      } finally {
        updateLoading('operations', false);
      }
    },
    [rbacService, showSuccess, showError, updateLoading, fetchRoles]
  );

  // User Permission Management
  const updateUserPermissions = useCallback(
    async (data: {
      userId: string;
      roleId?: string;
      permissions?: Array<{ resource: string; actions: string[] }>;
    }) => {
      updateLoading('operations', true);
      try {
        const response = await rbacService.updateUserPermissions(data);
        if (response.success) {
          showSuccess('User permissions updated successfully');
          return true;
        } else {
          showError(
            response.error?.message || 'Failed to update user permissions'
          );
          return false;
        }
      } catch (error) {
        showError('Error updating user permissions');
        return false;
      } finally {
        updateLoading('operations', false);
      }
    },
    [rbacService, showSuccess, showError, updateLoading]
  );

  const revokeUserPermissions = useCallback(
    async (data: { userId: string; permissionKeys: string[] }) => {
      updateLoading('operations', true);
      try {
        const response = await rbacService.revokeUserPermissions(data);
        if (response.success) {
          showSuccess('User permissions revoked successfully');
          return true;
        } else {
          showError(
            response.error?.message || 'Failed to revoke user permissions'
          );
          return false;
        }
      } catch (error) {
        showError('Error revoking user permissions');
        return false;
      } finally {
        updateLoading('operations', false);
      }
    },
    [rbacService, showSuccess, showError, updateLoading]
  );

  const bulkUpdateUserPermissions = useCallback(
    async (data: BulkUpdateRequest) => {
      updateLoading('operations', true);
      try {
        const response = await rbacService.bulkUpdateUserPermissions(data);
        if (response.success) {
          const successCount =
            response.data?.results?.filter(r => r.success).length || 0;
          const totalCount = response.data?.results?.length || 0;
          showSuccess(
            `${successCount}/${totalCount} user permissions updated successfully`
          );
          return {
            success: true,
            results: response.data?.results,
          };
        } else {
          showError('Failed to bulk update user permissions');
          return { success: false };
        }
      } catch (error) {
        showError('Error bulk updating user permissions');
        return { success: false };
      } finally {
        updateLoading('operations', false);
      }
    },
    [rbacService, showSuccess, showError, updateLoading]
  );

  // Permission Queries
  const getUserPermissions = useCallback(
    async (userId: string) => {
      updateLoading('userPermissions', true);
      try {
        const response = await rbacService.getUserPermissions(userId);
        if (response.success && response.data) {
          return response.data.permissions;
        } else {
          showError('Failed to fetch user permissions');
          return null;
        }
      } catch (error) {
        showError('Error fetching user permissions');
        return null;
      } finally {
        updateLoading('userPermissions', false);
      }
    },
    [rbacService, showError, updateLoading]
  );

  const getUsersWithPermission = useCallback(
    async (resource: ResourceName, action: ActionName) => {
      try {
        const response = await rbacService.getUsersWithPermission({
          resource,
          action,
        });
        if (response.success && response.data) {
          return response.data.users;
        } else {
          showError('Failed to fetch users with permission');
          return null;
        }
      } catch (error) {
        showError('Error fetching users with permission');
        return null;
      }
    },
    [rbacService, showError]
  );

  const fetchRoleHierarchy = useCallback(async () => {
    try {
      const response = await rbacService.getRoleHierarchy();
      if (response.success && response.data) {
        return response.data.hierarchy;
      } else {
        showError('Failed to fetch role hierarchy');
        return null;
      }
    } catch (error) {
      showError('Error fetching role hierarchy');
      return null;
    }
  }, [rbacService, showError]);

  const fetchPermissionStatistics = useCallback(async () => {
    updateLoading('statistics', true);
    try {
      const response = await rbacService.getPermissionStatistics();
      if (response.success && response.data) {
        setStatistics(response.data.stats);
      } else {
        showError('Failed to fetch permission statistics');
      }
    } catch (error) {
      showError('Error fetching permission statistics');
    } finally {
      updateLoading('statistics', false);
    }
  }, [rbacService, showError, updateLoading]);

  // Utility functions
  const hasPermission = useCallback(
    (
      userPermissions: Permission[],
      resource: ResourceName,
      action: ActionName
    ) => {
      return rbacService.hasPermission(userPermissions, resource, action);
    },
    [rbacService]
  );

  const mergePermissions = useCallback(
    (...permissionArrays: Permission[][]) => {
      return rbacService.mergePermissions(...permissionArrays);
    },
    [rbacService]
  );

  const validateRoleData = useCallback(
    (roleData: {
      name: string;
      permissions: Array<{ resource: string; actions: string[] }>;
    }) => {
      return rbacService.validateRoleData(roleData);
    },
    [rbacService]
  );

  return {
    // State
    roles,
    loading,
    statistics,

    // Role Management
    fetchRoles,
    createRole,
    updateRole,
    deleteRole,

    // User Permission Management
    updateUserPermissions,
    revokeUserPermissions,
    bulkUpdateUserPermissions,

    // Permission Queries
    getUserPermissions,
    getUsersWithPermission,
    fetchRoleHierarchy,
    fetchPermissionStatistics,

    // Utility functions
    hasPermission,
    mergePermissions,
    validateRoleData,
  };
};
